// Lightweight, privacy-friendly first-party traffic tracker.
//
// Fires ONLY for anonymous visitors — the moment a userInfo token exists in
// localStorage (employee / HR / admin), tracking is suppressed. If an anonymous
// visitor later logs in, excludeVisitor() flags all of their prior events.
//
// Per page it also measures engagement: time-on-page and max scroll depth are
// sent as a beacon when the visitor leaves the page or navigates away.
import axios from 'axios';

const BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';

const VISITOR_KEY = 'avani_visitor_id';
const SESSION_KEY = 'avani_session_id';
const ENTRY_KEY = 'avani_entry_page';
const UTM_KEY = 'avani_utm';

// Campaign attribution params we persist so a lead can be credited to the ad /
// channel that brought the visitor in — even after they navigate off the
// landing URL and the query string is gone.
const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];

const randomId = (prefix) => prefix + '_' + Math.random().toString(36).slice(2) + Date.now().toString(36);

const ls = {
  get: (k) => { try { return localStorage.getItem(k); } catch (_) { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch (_) {} },
};
const ss = {
  get: (k) => { try { return sessionStorage.getItem(k); } catch (_) { return null; } },
  set: (k, v) => { try { sessionStorage.setItem(k, v); } catch (_) {} },
};

export const getVisitorId = () => {
  let id = ls.get(VISITOR_KEY);
  if (!id) { id = randomId('v'); ls.set(VISITOR_KEY, id); }
  return id;
};

const getSessionId = () => {
  let id = ss.get(SESSION_KEY);
  if (!id) { id = randomId('s'); ss.set(SESSION_KEY, id); }
  return id;
};

// First page of this visitor's session — used to attribute leads to a landing page.
export const getEntryPage = () => ss.get(ENTRY_KEY) || (typeof window !== 'undefined' ? window.location.pathname : '');

// Capture UTM / click-id params the FIRST time we see them this session and keep
// them (first-touch wins). Safe to call on every pageview — it no-ops once set.
export const captureUtmParams = () => {
  try {
    if (ss.get(UTM_KEY)) return; // first-touch only — don't overwrite
    const q = new URLSearchParams(window.location.search);
    const found = {};
    UTM_FIELDS.forEach((f) => { const v = q.get(f); if (v) found[f] = v.slice(0, 120); });
    if (Object.keys(found).length) ss.set(UTM_KEY, JSON.stringify(found));
  } catch (_) {}
};

// The captured campaign attribution for this session ({} if organic/direct).
export const getUtmParams = () => {
  try { return JSON.parse(ss.get(UTM_KEY) || '{}'); } catch (_) { return {}; }
};

const isLoggedIn = () => !!ls.get('userInfo');

// ---- per-page engagement state ----
let currentId = null;
let pageStart = 0;
let maxScroll = 0;
let listenersBound = false;

const computeScroll = () => {
  try {
    const h = document.documentElement;
    const total = (h.scrollHeight - h.clientHeight) || 1;
    const pct = Math.round(((window.scrollY || h.scrollTop) / total) * 100);
    return Math.max(0, Math.min(100, pct));
  } catch (_) { return 0; }
};

// Send the finished page's engagement (time + scroll) via sendBeacon so it
// survives the page being unloaded.
const finalizePage = () => {
  if (!currentId) return;
  const durationMs = Date.now() - pageStart;
  const payload = JSON.stringify({ id: currentId, durationMs, maxScroll });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${BASE}/analytics/duration`, new Blob([payload], { type: 'application/json' }));
    } else {
      axios.post(`${BASE}/analytics/duration`, JSON.parse(payload)).catch(() => {});
    }
  } catch (_) {}
  currentId = null;
};

const bindListeners = () => {
  if (listenersBound) return;
  listenersBound = true;
  window.addEventListener('scroll', () => { const s = computeScroll(); if (s > maxScroll) maxScroll = s; }, { passive: true });
  // Page hidden / closed → flush engagement for the current page.
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') finalizePage(); });
  window.addEventListener('pagehide', finalizePage);
};

// Record one pageview. No-op for logged-in users.
export const trackPageview = (path, title) => {
  captureUtmParams(); // first-touch attribution — capture before the logged-in short-circuit
  if (isLoggedIn()) return;
  bindListeners();
  if (!ss.get(ENTRY_KEY)) ss.set(ENTRY_KEY, path);

  // Flush the previous page's engagement before starting a new one.
  finalizePage();
  maxScroll = computeScroll();
  pageStart = Date.now();

  try {
    axios.post(`${BASE}/analytics/track`, {
      visitorId: getVisitorId(),
      sessionId: getSessionId(),
      path,
      title: title || document.title,
      referrer: document.referrer || '',
    }).then((res) => { currentId = res?.data?.id || null; }).catch(() => {});
  } catch (_) {}
};

// Called on login — exclude this visitor's anonymous history from analytics.
export const excludeVisitor = () => {
  try {
    const id = ls.get(VISITOR_KEY);
    if (!id) return;
    axios.post(`${BASE}/analytics/exclude`, { visitorId: id }).catch(() => {});
  } catch (_) {}
};

// Record use of a free public calculator/generator tool.
//   action: 'view' (opened the tool) | 'download' (printed/saved a result)
//   data:   the submission entered (amounts, selections and, on generators, the
//           full form incl. names). Stored CONFIDENTIALLY for the organisation
//           and shown only in the HR/admin Calculator Report.
// Fire-and-forget; never tracks logged-in users (same rule as pageview tracking).
export const trackCalculatorUse = (slug, name, action, data) => {
  try {
    if (isLoggedIn()) return;
    const payload = JSON.stringify({
      slug,
      name,
      action: action || 'view',
      data: data && typeof data === 'object' ? data : {},
      visitorId: getVisitorId(),
      sessionId: getSessionId(),
      landingPage: getEntryPage(),
      referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${BASE}/analytics/calculator`, new Blob([payload], { type: 'application/json' }));
    } else {
      axios.post(`${BASE}/analytics/calculator`, JSON.parse(payload)).catch(() => {});
    }
  } catch (_) {}
};
